import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateTag } from "../hooks/useTags";
import {
  TAG_COLOR_OPTIONS,
  tagSwatchClassName,
  tagSwatchStyle,
} from "../lib/tagColors";
import { tagIconId } from "../lib/tagIcons";
import type { Tag } from "../types";
import { Icon } from "@/components/ui/Icon";
import { TagIconPicker } from "./TagIconPicker";

interface TagEditorProps {
  tag: Tag;
}

export function TagEditor({ tag }: TagEditorProps) {
  const { t } = useTranslation();
  const updateTag = useUpdateTag();
  const initialIcon = tag.icon && tagIconId(tag.icon) ? tag.icon : null;

  const [selectedIcon, setSelectedIcon] = useState<string | null>(
    initialIcon,
  );
  const [selectedColor, setSelectedColor] = useState<string | null>(
    tag.color ?? null,
  );

  const save = (icon: string | null, color: string | null) => {
    updateTag.mutate({ id: tag.id, icon, color });
  };

  return (
    <div className="p-3 space-y-3">
      {/* Icon picker */}
      <div>
        <p className="mb-2 text-xs font-medium text-muted">
          {t("tagEditor.icon")}
        </p>
        <TagIconPicker
          value={selectedIcon}
          onChange={(next) => {
            setSelectedIcon(next);
            save(next, selectedColor);
          }}
        />
      </div>

      {/* Color picker */}
      <div>
        <p className="mb-2 text-xs font-medium text-muted">
          {t("tagEditor.color")}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {TAG_COLOR_OPTIONS.map(({ name, value }) => (
            <button
              key={name}
              onClick={() => {
                const next = selectedColor === value ? null : value;
                setSelectedColor(next);
                save(selectedIcon, next);
              }}
              title={name}
              className={cn(
                "relative h-6 w-6 rounded-full transition-transform duration-100 hover:scale-110",
                tagSwatchClassName(value),
              )}
              style={tagSwatchStyle(value)}
            >
              {selectedColor === value && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Icon name="check" size={12} className="text-on-accent drop-shadow" />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
